"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type SaveOptions = { automatic?: boolean; force?: boolean; drain?: boolean };
interface AutosaveOptions {
    persist: (options: { automatic: boolean }) => Promise<void>;
    enabled?: boolean;
    delay?: number;
    minInterval?: number;
}

/** Persistence mechanics shared by chapter and wiki editors. Navigation and
 * publication policy belong to the caller. Failed saves never discard edits. */
export function useAutosave({ persist, enabled = true, delay = 1000, minInterval = 0 }: AutosaveOptions) {
    const options = useRef({ persist, enabled, delay, minInterval });
    const revision = useRef(0);
    const savedRevision = useRef(0);
    const generation = useRef(0);
    const pending = useRef<Promise<boolean> | null>(null);
    const pendingGeneration = useRef(0);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scheduleVersion = useRef(0);
    const lastEditAt = useRef(-Infinity);
    const lastSaveStartedAt = useRef(-Infinity);
    const mounted = useRef(true);
    const [state, setState] = useState({ isDirty: false, isSaving: false, error: null as string | null });

    useLayoutEffect(() => { options.current = { persist, enabled, delay, minInterval }; });
    const cancelPending = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
        scheduleVersion.current += 1;
    }, []);
    const updateState = useCallback((error: string | null = null) => {
        if (mounted.current) setState({
            isDirty: revision.current !== savedRevision.current,
            isSaving: pending.current !== null,
            error,
        });
    }, []);

    const flush = useCallback(async ({ automatic = false, force = false, drain = true }: SaveOptions = {}): Promise<boolean> => {
        const origin = generation.current;
        cancelPending();
        // Recheck after every await: multiple callers can be waiting together.
        while (pending.current) {
            const previousGeneration = pendingGeneration.current;
            const saved = await pending.current;
            if (origin !== generation.current) return false;
            if (!saved && automatic && previousGeneration === origin) return false;
        }
        if (automatic && !options.current.enabled) return revision.current === savedRevision.current;
        if (!force && revision.current === savedRevision.current) return true;
        // Start in a microtask so the lock exists even if persist throws synchronously.
        const request = Promise.resolve().then(async () => {
            try {
                do {
                    if (origin !== generation.current) return false;
                    if (automatic && !options.current.enabled) return revision.current === savedRevision.current;
                    const snapshotRevision = revision.current;
                    lastSaveStartedAt.current = Date.now();
                    await options.current.persist({ automatic });
                    if (origin !== generation.current) return false;
                    savedRevision.current = snapshotRevision;
                    updateState();
                } while (drain && revision.current !== savedRevision.current);
                return true;
            } catch (error) {
                if (origin === generation.current) {
                    cancelPending();
                    updateState(error instanceof Error ? error.message : "Couldn't save. Please try again.");
                }
                return false;
            }
        });
        pendingGeneration.current = origin;
        pending.current = request;
        updateState();
        try {
            return await request;
        } finally {
            if (pending.current === request) pending.current = null;
            if (mounted.current) setState(current => ({
                ...current,
                isDirty: revision.current !== savedRevision.current,
                isSaving: pending.current !== null,
            }));
        }
    }, [cancelPending, updateState]);

    const scheduleAutosave = useCallback(() => {
        cancelPending();
        if (!options.current.enabled) return;
        const version = scheduleVersion.current;
        const origin = generation.current;
        const remainingDelay = () => Math.max(0,
            lastEditAt.current + options.current.delay - Date.now(),
            lastSaveStartedAt.current + options.current.minInterval - Date.now());
        const runWhenReady = async () => {
            timer.current = null;
            while (pending.current) {
                const previousGeneration = pendingGeneration.current;
                const saved = await pending.current;
                if (version !== scheduleVersion.current || origin !== generation.current) return;
                if (!saved && previousGeneration === origin) return;
            }
            // New typing, explicit flushes, resets and unmounts invalidate a
            // timer even when it has already started waiting on a request.
            if (!mounted.current || version !== scheduleVersion.current || origin !== generation.current) return;
            if (!options.current.enabled || revision.current === savedRevision.current) return;
            const remaining = remainingDelay();
            if (remaining > 0) timer.current = setTimeout(() => { void runWhenReady(); }, remaining);
            else await flush({ automatic: true, drain: false });
        };
        timer.current = setTimeout(() => { void runWhenReady(); }, remainingDelay());
    }, [cancelPending, flush]);

    const markDirty = useCallback(() => {
        revision.current += 1;
        lastEditAt.current = Date.now();
        updateState();
        scheduleAutosave();
    }, [scheduleAutosave, updateState]);
    const reset = useCallback(() => {
        cancelPending();
        generation.current += 1;
        revision.current += 1;
        savedRevision.current = revision.current;
        lastSaveStartedAt.current = -Infinity;
        updateState();
    }, [cancelPending, updateState]);
    const getRevision = useCallback(() => revision.current, []);
    const hasPendingChanges = useCallback(() => revision.current !== savedRevision.current || pending.current !== null, []);
    const waitForPending = useCallback(() => pending.current ?? Promise.resolve(true), []);

    useEffect(() => {
        if (!enabled) cancelPending();
        else if (revision.current !== savedRevision.current && !timer.current) scheduleAutosave();
    }, [enabled, cancelPending, scheduleAutosave]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; cancelPending(); };
    }, [cancelPending]);

    return { ...state, markDirty, flush, cancelPending, reset, getRevision, hasPendingChanges, waitForPending };
}
